import { apiClient } from "@/lib/api/client";
import { assign, fromPromise, setup } from "xstate";

export type SearchSoftwareOption = {
  id: string; // software.id
  value: string; // software.id
  label: string; // software.name
  description?: string; // external tenant name
  disabled?: boolean;
};
type SearchSoftwareOutput = SearchSoftwareOption[];
type SearchSoftwareInput = { searchText: string };

type GetSoftwareOutput = SearchSoftwareOption;
type GetSoftwareInput = { softwareId: string };

const searchSoftwareActor = fromPromise<
  SearchSoftwareOutput,
  SearchSoftwareInput
>(async ({ input }) => {
  try {
    const res = await apiClient.GET("/v2/software", {
      params: { query: { freeText: input.searchText, pageSize: 5 } },
    });
    if (!res.data?.items) return [];
    return res.data.items
      .filter((s) => (s.id || s.appIntegrationId) && s.name)
      .map((software) => ({
        id: software.id || software.appIntegrationId || "",
        value: software.id || software.appIntegrationId || "",
        label: software.name || "",
        description: software.externalTenantName ?? undefined,
      }));
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "";
    throw new Error(errorMessage, { cause: error as Error });
  }
});

const getSoftwareActor = fromPromise<GetSoftwareOutput, GetSoftwareInput>(
  async ({ input }) => {
    try {
      const res = await apiClient.GET("/v2/software", {
        params: {
          query: {
            idsIn: [input.softwareId],
          },
        },
      });
      const software = res.data?.items.find(
        (s) =>
          s.id === input.softwareId || s.appIntegrationId === input.softwareId,
      );
      if (!software) throw new Error("Software not found");
      return {
        id: software.id || "",
        value: software.id || "",
        label: software.name || "",
        description: software.externalTenantName ?? undefined,
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "";
      throw new Error(errorMessage);
    }
  },
);

type Context = {
  selectedSoftwareId: string;
  selectedSoftwareOption: SearchSoftwareOption | null;
  searchText: string;
  searchResults: SearchSoftwareOption[];
  errorMessage: string | null;
};

type Event =
  | { type: "INIT" }
  | { type: "GET_SOFTWARE"; softwareId: string }
  | { type: "SEARCH_SOFTWARE"; text: string }
  | {
      type: "SELECT_SOFTWARE";
      softwareId: string;
      softwareOption: SearchSoftwareOption;
    }
  | { type: "REMOVE_SOFTWARE"; softwareId: string };

type Input = {
  selectedSoftwareId?: string;
};

const initialContext: Context = {
  selectedSoftwareId: "",
  selectedSoftwareOption: null,
  searchText: "",
  searchResults: [],
  errorMessage: null,
};

export const createSoftwareSelectMachine = () => {
  return setup({
    types: {
      context: {} as Context,
      events: {} as Event,
      input: {} as Input,
    },
    actors: {
      searchSoftware: searchSoftwareActor,
      getSoftware: getSoftwareActor,
    },
    actions: {},
    guards: {
      isInitializedWithSoftwareId: ({ context }) =>
        !!context.selectedSoftwareId && context.selectedSoftwareOption == null,
    },
  }).createMachine({
    /** @xstate-layout N4IgpgJg5mDOIC5SzAQwE4GMAWAFM6sA9gHYB0AliRQC4DEAkgHIMAqA2gAwC6ioADkVi0KpPiAAeiAIwAmAKxkAnNM7yls2QA5ZnLRoA0IAJ6JZAZgBsZeZ0sB2VbIAs0+5dmWtAX29GUGDj4hKSU1PTMbOzSvEgggsI0oiTiUghyiipqGtq6+rJGpgha0mTSJVrOSu5eskryzr7+aFh4BMTkKAA2YJhJJFDBHXQAygCiAIIASgDCABIA+rhjUyMA8kxcsQJCImJxaZbyiqruVp7meuqFiOZa5mT38vZ3deae5Y1+IAGtQ6HdXr9QbtUijMYAGTGM1YSxW602PHECT2KQOiC0RxsWnuei07mcznuNwQsjcZHc0ks9Vs7nk0juTR+LSCoM6YB6fSoIJCJDoUzGAFk1gA1MZw1YbLbI3ZJfagNKY2RlezPczSZyaSycZz2ElyB62Tx2ZyWcxEqxKJm-Vm8sgwGj-PkQUhgMIANyIAGs3Ta2naHU6EFRPZhUHKSFtpXEURHUohLESyJxpBkGZxzOoySTdKVLvY1BqSpYvF5rSz-R17WBHWy6AR0ER0GR+F1wwAzJsAWzIfqd1drvODJFD4eSUaRMdlyXjCETWmTqYU6czGmkOY0Nkc5iURPxpvknnLgUrAIr3KddBdJDdIe9vor-b9F7Zw9HEYn23i0-lkgTSZTNNLlXbMTEQXUF04XcdBecx7E1eovmaE8n3PAZLwbJsWzbGhO3QHs+zZXs0J5Do3yIMMPx4aMdkSGd0TnACl3pYCs3XMCEHkakyBAnV3jkVV5F8b4SCICA4HEQjeRlOjfzSABaSwSUU48-iIqhaBk1FZ01El8TIXV7BUeCcWkDRd1U20q0BLl0LZLS4wY-FOBsBQrHMTMoNsZwSXVexk10exKmOcozOkSzT3IQN7KnWS0QVcDLgpVMjmkBpMy8JQcwZZN5DuPQC1NBCItQk8X2k2LtIY1wXPUNKSg85xznYopnE4ZUNA8MkaTazwvl8IA */
    id: "searchSoftware",
    initial: "init",
    context: ({ input }) => ({
      ...initialContext,
      selectedSoftwareId: input.selectedSoftwareId ?? "",
    }),
    states: {
      init: {
        on: {
          INIT: [
            {
              guard: "isInitializedWithSoftwareId",
              target: "getSoftware",
            },
            {
              target: "selectingSoftware",
            },
          ],
        },
      },
      selectingSoftware: {
        on: {
          SEARCH_SOFTWARE: {
            actions: assign({
              searchText: ({ event }) => event.text,
            }),
            target: "searchingSoftware",
          },
          SELECT_SOFTWARE: {
            actions: assign({
              selectedSoftwareId: ({ event }) => event.softwareId,
              selectedSoftwareOption: ({ event }) => event.softwareOption,
              searchText: "",
              searchResults: [],
              errorMessage: null,
            }),
          },
          REMOVE_SOFTWARE: {
            actions: assign({
              selectedSoftwareId: () => "",
              selectedSoftwareOption: null,
              errorMessage: null,
            }),
          },
        },
      },
      getSoftware: {
        invoke: {
          src: "getSoftware",
          input: ({ context }) => ({
            softwareId: context.selectedSoftwareId,
          }),
          onDone: [
            {
              target: "selectingSoftware",
              actions: assign({
                selectedSoftwareOption: ({ event }) => event.output,
              }),
            },
          ],
          onError: [
            {
              target: "selectingSoftware",
              actions: assign({
                errorMessage: ({ event }) => {
                  const error = event.error as Error;
                  return error?.message || "";
                },
                searchResults: [],
              }),
            },
          ],
        },
      },
      searchingSoftware: {
        invoke: {
          src: "searchSoftware",
          input: ({ context }) => ({
            searchText: context.searchText,
          }),
          onDone: [
            {
              target: "selectingSoftware",
              actions: assign({
                searchResults: ({ event }) => event.output,
              }),
            },
          ],
          onError: [
            {
              target: "selectingSoftware",
              actions: assign({
                errorMessage: ({ event }) => {
                  const error = event.error as Error;
                  return error?.message || "";
                },
                searchResults: [],
              }),
            },
          ],
        },
      },
    },
  });
};
